import React, { useState, useEffect, useCallback } from "react";
import {
  SimpleButton,
  LoadingButton,
  Input,
  TextArea,
  Select,
  NotificationsList,
  Spinner,
  useStateContext,
  useHttp,
} from "@veda/dashboard-ui";
import { BsPlus, BsCheckCircle, BsXCircle, BsClockHistory, BsDownload, BsFileEarmarkText } from "react-icons/bs";
import { usePppApi } from "../services/pppApi";
import { useAuthUser } from "../hooks/useAuthUser";
import PppDialog from "./PppDialog";

// Estrae i messaggi di validazione (422) dalla risposta useHttp.onFormError:
// preferisce i messaggi per-campo del backend, con fallback al messaggio generico.
function estraiErrori(err) {
  if (err?.errors) return Object.values(err.errors).flat();
  return [err?.message ?? "Errore durante il salvataggio."];
}

const STATO_RICHIESTA_CFG = {
  in_attesa:              { cls: "bg-yellow-100 text-yellow-800", label: "In attesa" },
  integrazione_ricevuta:  { cls: "bg-green-100 text-green-800",  label: "Integrazione ricevuta" },
  respinta:               { cls: "bg-red-100 text-red-800",       label: "Respinta" },
};

const STATO_CDS_CFG = {
  programmata: { cls: "bg-blue-100 text-blue-800",   label: "Programmata" },
  in_corso:    { cls: "bg-yellow-100 text-yellow-800", label: "In corso" },
  conclusa:    { cls: "bg-green-100 text-green-800",  label: "Conclusa" },
  annullata:   { cls: "bg-gray-100 text-gray-600",   label: "Annullata" },
};

const TIPI_INTEGRAZIONE = [
  { value: "progetto_fattibilita",        label: "Progetto di Fattibilità" },
  { value: "piano_economico_finanziario", label: "Piano Economico-Finanziario" },
  { value: "schema_convenzione",          label: "Schema di Convenzione" },
  { value: "altri_documenti",             label: "Altri Documenti" },
];

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("it-IT");
}

function StatoBadge({ cfg, stato }) {
  const c = cfg[stato] ?? { cls: "", label: stato };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${c.cls}`}>
      {c.label}
    </span>
  );
}

function RichiestaRow({ richiesta, onDownload, onRegistraRisposta, onRespingi, canGestisci }) {
  const scaduta = richiesta.scaduta && richiesta.stato === "in_attesa";
  const documenti = richiesta.documenti ?? [];
  return (
    <div className={`border rounded-lg p-4 ${scaduta ? "border-red-300 bg-red-50" : "border-gray-200 bg-white"}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <StatoBadge cfg={STATO_RICHIESTA_CFG} stato={richiesta.stato} />
            {scaduta && (
              <span className="text-xs font-semibold text-red-700 flex items-center gap-1">
                <BsClockHistory /> Scaduta
              </span>
            )}
            <span className="text-xs text-gray-500">
              Termine: <span className="font-medium text-gray-800">{formatDate(richiesta.termine_risposta)}</span>
            </span>
          </div>
          <div className="flex flex-wrap gap-1 mb-2">
            {(richiesta.tipi_integrazione ?? []).map((t) => {
              const cfg = TIPI_INTEGRAZIONE.find((x) => x.value === t);
              return (
                <span key={t} className="px-2 py-0.5 bg-indigo-100 text-indigo-800 text-xs rounded">
                  {cfg?.label ?? t}
                </span>
              );
            })}
          </div>
          {richiesta.descrizione && (
            <p className="text-sm text-gray-700 mb-1 whitespace-pre-wrap">{richiesta.descrizione}</p>
          )}
          <p className="text-xs text-gray-400">
            Richiesta da <span className="font-medium">{richiesta.richiesta_da ?? "—"}</span>{" "}
            il {formatDate(richiesta.richiesta_il)}
          </p>
          {richiesta.note_risposta && (
            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-800">
              <span className="font-semibold">Risposta:</span> {richiesta.note_risposta}
              {richiesta.risposta_ricevuta_il && ` — ${formatDate(richiesta.risposta_ricevuta_il)}`}
            </div>
          )}
          {documenti.length > 0 && (
            <div className="mt-2">
              <p className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
                <BsFileEarmarkText /> Documenti integrativi caricati dall'operatore:
              </p>
              <ul className="space-y-1">
                {documenti.map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-2 text-xs bg-gray-50 border border-gray-200 rounded px-2 py-1">
                    <span className="truncate text-gray-700">{d.nome}</span>
                    <button
                      type="button"
                      onClick={() => onDownload(d.id, d.nome)}
                      className="shrink-0 inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                    >
                      <BsDownload /> Scarica
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {richiesta.note_respingimento && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
              <span className="font-semibold">Respingimento:</span> {richiesta.note_respingimento}
              {richiesta.respingimento_automatico && " (automatico per mancata risposta)"}
            </div>
          )}
        </div>
        {richiesta.stato === "in_attesa" && canGestisci && (
          <div className="flex flex-col gap-2 shrink-0">
            <SimpleButton
              text="Registra risposta"
              icon={<BsCheckCircle />}
              customFunc={() => onRegistraRisposta(richiesta)}
            />
            <SimpleButton
              text="Respingi"
              icon={<BsXCircle />}
              customFunc={() => onRespingi(richiesta)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function ConferenzaRow({ conferenza }) {
  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <StatoBadge cfg={STATO_CDS_CFG} stato={conferenza.stato} />
        <span className="text-xs text-gray-500">
          Tipo: <span className="font-medium capitalize">{conferenza.tipo}</span>
        </span>
        {conferenza.data_convocazione && (
          <span className="text-xs text-gray-500">
            Convocazione: <span className="font-medium">{formatDate(conferenza.data_convocazione)}</span>
          </span>
        )}
        {conferenza.termine_determinazioni_giorni && (
          <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
            Termine determinazioni: {conferenza.termine_determinazioni_giorni} gg
          </span>
        )}
      </div>
      {conferenza.motivazione_attivazione && (
        <p className="text-sm text-gray-700 mb-2">{conferenza.motivazione_attivazione}</p>
      )}
      {(conferenza.amministrazioni_coinvolte ?? []).length > 0 && (
        <div className="mb-2">
          <p className="text-xs font-semibold text-gray-600 mb-1">Amministrazioni coinvolte:</p>
          <div className="flex flex-wrap gap-1">
            {conferenza.amministrazioni_coinvolte.map((a, i) => (
              <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">{a}</span>
            ))}
          </div>
        </div>
      )}
      {conferenza.determinazione_finale && (
        <div className="mt-2 p-2 bg-teal-50 border border-teal-200 rounded text-xs text-teal-800">
          <span className="font-semibold">Determinazione finale:</span>{" "}
          {conferenza.determinazione_finale}
        </div>
      )}
    </div>
  );
}

export default function Fase10Tab({ procedimentoId, propostaId }) {
  const { addToast } = useStateContext();
  const api = usePppApi();
  const { request } = useHttp();
  const { can } = useAuthUser();

  // Download autenticato (cookie/CSRF) via blob: un <a href> diretto non passa
  // l'auth e verrebbe rediretto alla pagina profilo.
  const handleDownloadDocumento = (documentoId, nome) => {
    request(api.scaricaDocumento(procedimentoId, documentoId), {
      method: 'GET',
      download: true,
      onSuccess: async (response) => {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = nome || 'documento';
        a.click();
        URL.revokeObjectURL(url);
      },
      onError: () => addToast({ type: 'error', message: 'Errore durante il download.' }),
    });
  };

  const [integrazioni, setIntegrazioni] = useState([]);
  const [conferenze, setConferenze]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);

  const [openRichiesta, setOpenRichiesta]         = useState(false);
  const [openRegistraRisposta, setOpenRegistraRisposta] = useState(null);
  const [openRespingi, setOpenRespingi]           = useState(null);
  const [openConferenza, setOpenConferenza]        = useState(false);
  const [richiestaErrors, setRichiestaErrors]     = useState(null);
  const [conferenzaErrors, setConferenzaErrors]   = useState(null);
  const [rispostaErrors, setRispostaErrors]       = useState(null);
  const [respingiErrors, setRespingiErrors]       = useState(null);

  const [richiestaForm, setRichiestaForm] = useState({
    tipi_integrazione: [],
    descrizione: "",
    termine_risposta: "",
  });

  const [rispostaForm, setRispostaForm] = useState({ note_risposta: "" });
  const [respingiForm, setRespingiForm] = useState({ note_respingimento: "" });

  const [conferenzaForm, setConferenzaForm] = useState({
    motivazione_attivazione: "",
    amministrazioni_coinvolte: "",
    termine_determinazioni_giorni: "30",
  });

  const load = useCallback(() => {
    setLoading(true);
    let done = 0;
    const check = () => { if (++done === 2) setLoading(false); };
    api.listIntegrazioni(procedimentoId, propostaId, {
      onSuccess: (r) => { if (r) setIntegrazioni(Array.isArray(r) ? r : (r.data ?? [])); check(); },
      onError:   () => check(),
    });
    api.listConferenzeServizi(procedimentoId, propostaId, {
      onSuccess: (r) => { if (r) setConferenze(Array.isArray(r) ? r : (r.data ?? [])); check(); },
      onError:   () => check(),
    });
  }, [procedimentoId, propostaId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleCreaRichiesta = () => {
    setRichiestaErrors(null);
    const errs = [];
    if (!richiestaForm.tipi_integrazione.length) errs.push("Selezionare almeno un tipo di integrazione.");
    if (!richiestaForm.termine_risposta) {
      errs.push("La data limite per la risposta è obbligatoria.");
    } else {
      // Il backend richiede una data successiva a oggi (after:today).
      const oggi = new Date(); oggi.setHours(0, 0, 0, 0);
      const termine = new Date(richiestaForm.termine_risposta);
      if (termine <= oggi) errs.push("Il termine per la risposta deve essere una data futura (successiva a oggi).");
    }
    if (errs.length) { setRichiestaErrors(errs); return; }

    setSaving(true);
    api.creaRichiestaIntegrazione(procedimentoId, propostaId, {
      tipi_integrazione: richiestaForm.tipi_integrazione,
      descrizione: richiestaForm.descrizione || undefined,
      termine_risposta: richiestaForm.termine_risposta,
    }, {
      onSuccess: () => {
        setSaving(false);
        setOpenRichiesta(false);
        setRichiestaForm({ tipi_integrazione: [], descrizione: "", termine_risposta: "" });
        addToast({ type: "success", message: "Richiesta di integrazione inviata." });
        load();
      },
      // Errori di validazione (422) arrivano su onFormError, non onError.
      onFormError: (err) => { setSaving(false); setRichiestaErrors(estraiErrori(err)); },
      onError: () => { setSaving(false); setRichiestaErrors(["Errore di rete durante il salvataggio."]); },
    });
  };

  const handleRegistraRisposta = () => {
    setRispostaErrors(null);
    setSaving(true);
    api.registraRispostaIntegrazione(procedimentoId, propostaId, openRegistraRisposta.id, {
      note_risposta: rispostaForm.note_risposta || undefined,
    }, {
      onSuccess: () => {
        setSaving(false);
        setOpenRegistraRisposta(null);
        setRispostaForm({ note_risposta: "" });
        addToast({ type: "success", message: "Risposta registrata." });
        load();
      },
      onFormError: (err) => { setSaving(false); setRispostaErrors(estraiErrori(err)); },
      onError: () => { setSaving(false); setRispostaErrors(["Errore di rete durante il salvataggio."]); },
    });
  };

  const handleRespingi = () => {
    setRespingiErrors(null);
    setSaving(true);
    api.respingiIntegrazione(procedimentoId, propostaId, openRespingi.id, {
      note_respingimento: respingiForm.note_respingimento || undefined,
    }, {
      onSuccess: () => {
        setSaving(false);
        setOpenRespingi(null);
        setRespingiForm({ note_respingimento: "" });
        addToast({ type: "success", message: "Proposta respinta per mancata integrazione." });
        load();
      },
      onFormError: (err) => { setSaving(false); setRespingiErrors(estraiErrori(err)); },
      onError: () => { setSaving(false); setRespingiErrors(["Errore di rete durante il salvataggio."]); },
    });
  };

  const handleAttivaConferenza = () => {
    setConferenzaErrors(null);
    const amministrazioni = conferenzaForm.amministrazioni_coinvolte
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    const errs = [];
    if (!conferenzaForm.motivazione_attivazione.trim()) errs.push("La motivazione è obbligatoria.");
    if (!amministrazioni.length) errs.push("Indicare almeno un'amministrazione coinvolta.");
    if (errs.length) { setConferenzaErrors(errs); return; }

    setSaving(true);
    api.attivaConferenzaServizi(procedimentoId, propostaId, {
      motivazione_attivazione: conferenzaForm.motivazione_attivazione,
      amministrazioni_coinvolte: amministrazioni,
      termine_determinazioni_giorni: parseInt(conferenzaForm.termine_determinazioni_giorni, 10),
    }, {
      onSuccess: () => {
        setSaving(false);
        setOpenConferenza(false);
        setConferenzaForm({ motivazione_attivazione: "", amministrazioni_coinvolte: "", termine_determinazioni_giorni: "30" });
        addToast({ type: "success", message: "Conferenza di Servizi Preliminare attivata." });
        load();
      },
      onError: () => {
        setSaving(false);
        setConferenzaErrors(["Errore durante il salvataggio."]);
      },
    });
  };

  const toggleTipo = (val) => {
    setRichiestaForm((f) => {
      const arr = f.tipi_integrazione.includes(val)
        ? f.tipi_integrazione.filter((x) => x !== val)
        : [...f.tipi_integrazione, val];
      return { ...f, tipi_integrazione: arr };
    });
  };

  if (loading) return <div className="flex justify-center py-12"><Spinner /></div>;

  return (
    <div className="space-y-8">
      {/* ── Sezione Integrazioni ──────────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Richiesta Integrazioni Documentali
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">Art. 193, co. 6 D.lgs 36/2023</p>
          </div>
          {can("ppp.integrazioni.create") && (
            <SimpleButton
              text="Nuova richiesta"
              icon={<BsPlus />}
              customFunc={() => setOpenRichiesta(true)}
            />
          )}
        </div>

        {integrazioni.length === 0 ? (
          <div className="text-center py-8 text-gray-400 border border-dashed border-gray-200 rounded-lg">
            Nessuna richiesta di integrazione documentale.
          </div>
        ) : (
          <div className="space-y-3">
            {integrazioni.map((r) => (
              <RichiestaRow
                key={r.id}
                richiesta={r}
                onDownload={handleDownloadDocumento}
                onRegistraRisposta={(x) => { setOpenRegistraRisposta(x); setRispostaErrors([]); }}
                onRespingi={(x) => { setOpenRespingi(x); setRespingiErrors([]); }}
                canGestisci={can("ppp.integrazioni.manage")}
              />
            ))}
          </div>
        )}
      </section>

      {/* ── Sezione Conferenza di Servizi ─────────────────────────────── */}
      {can("ppp.conferenza_servizi.view") && <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Conferenza di Servizi Preliminare (facoltativa)
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">Art. 14, co. 3 L. 241/1990</p>
          </div>
          {can("ppp.conferenza_servizi.create") && (
            <SimpleButton
              text="Attiva CdS"
              icon={<BsPlus />}
              customFunc={() => setOpenConferenza(true)}
            />
          )}
        </div>

        {conferenze.length === 0 ? (
          <div className="text-center py-8 text-gray-400 border border-dashed border-gray-200 rounded-lg">
            Nessuna conferenza di servizi attivata.
          </div>
        ) : (
          <div className="space-y-3">
            {conferenze.map((c) => <ConferenzaRow key={c.id} conferenza={c} />)}
          </div>
        )}
      </section>}

      {/* Dialog nuova richiesta integrazione */}
      <PppDialog
        open={openRichiesta}
        title="Nuova richiesta di integrazione documentale"
        size="w-[600px]"
        onClose={() => { setOpenRichiesta(false); setRichiestaErrors(null); }}
      >
        <div className="space-y-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-800">
              La richiesta sarà notificata al Promotore/Proponente. Il mancato rispetto del termine
              comporta il respingimento automatico della proposta (art. 193, co. 6 D.lgs 36/2023).
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Tipi di integrazione richiesti *</p>
            <div className="grid grid-cols-2 gap-2">
              {TIPI_INTEGRAZIONE.map((t) => (
                <label key={t.value} className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={richiestaForm.tipi_integrazione.includes(t.value)}
                    onChange={() => toggleTipo(t.value)}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-700">{t.label}</span>
                </label>
              ))}
            </div>
          </div>
          <TextArea
            label="Descrizione delle integrazioni richieste"
            rows={4}
            value={richiestaForm.descrizione}
            onChange={(e) => setRichiestaForm((f) => ({ ...f, descrizione: e.target.value }))}
            placeholder="Descrivere in dettaglio cosa si richiede al Promotore/Proponente..."
          />
          <Input
            type="date"
            label="Termine per la risposta *"
            value={richiestaForm.termine_risposta}
            onChange={(e) => setRichiestaForm((f) => ({ ...f, termine_risposta: e.target.value }))}
          />
          {richiestaErrors?.length > 0 && <NotificationsList errors={richiestaErrors} />}
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <SimpleButton
              text="Annulla"
              customFunc={() => { setOpenRichiesta(false); setRichiestaErrors(null); }}
            />
            <LoadingButton
              isLoading={saving}
              customFunc={handleCreaRichiesta}
              text="Invia richiesta"
            />
          </div>
        </div>
      </PppDialog>

      {/* Dialog registra risposta */}
      <PppDialog
        open={!!openRegistraRisposta}
        title="Registra risposta integrazione"
        size="w-[480px]"
        onClose={() => setOpenRegistraRisposta(null)}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Confermando, la richiesta verrà marcata come <strong>integrazione ricevuta</strong>.
          </p>
          <TextArea
            label="Note sulla risposta ricevuta"
            rows={3}
            value={rispostaForm.note_risposta}
            onChange={(e) => setRispostaForm((f) => ({ ...f, note_risposta: e.target.value }))}
          />
          {rispostaErrors?.length > 0 && <NotificationsList errors={rispostaErrors} />}
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <SimpleButton text="Annulla" customFunc={() => setOpenRegistraRisposta(null)} />
            <LoadingButton isLoading={saving} customFunc={handleRegistraRisposta} text="Conferma" />
          </div>
        </div>
      </PppDialog>

      {/* Dialog respingi */}
      <PppDialog
        open={!!openRespingi}
        title="Respingi proposta per mancata integrazione"
        size="w-[480px]"
        onClose={() => setOpenRespingi(null)}
      >
        <div className="space-y-4">
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-xs text-red-800">
              Il respingimento è irreversibile. La proposta verrà marcata come respinta per
              mancata risposta alla richiesta di integrazione documentale.
            </p>
          </div>
          <TextArea
            label="Note sul respingimento"
            rows={3}
            value={respingiForm.note_respingimento}
            onChange={(e) => setRespingiForm((f) => ({ ...f, note_respingimento: e.target.value }))}
          />
          {respingiErrors?.length > 0 && <NotificationsList errors={respingiErrors} />}
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <SimpleButton text="Annulla" customFunc={() => setOpenRespingi(null)} />
            <LoadingButton isLoading={saving} customFunc={handleRespingi} text="Respingi proposta" />
          </div>
        </div>
      </PppDialog>

      {/* Dialog attiva conferenza di servizi */}
      <PppDialog
        open={openConferenza}
        title="Attiva Conferenza di Servizi Preliminare"
        size="w-[580px]"
        onClose={() => { setOpenConferenza(false); setConferenzaErrors(null); }}
      >
        <div className="space-y-4">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-800">
              La conferenza sarà convocata entro 5 giorni lavorativi (art. 14, co. 3 L. 241/1990).
              Le determinazioni devono essere adottate entro il termine selezionato.
            </p>
          </div>
          <TextArea
            label="Motivazione dell'attivazione *"
            rows={4}
            value={conferenzaForm.motivazione_attivazione}
            onChange={(e) => setConferenzaForm((f) => ({ ...f, motivazione_attivazione: e.target.value }))}
            placeholder="Indicare le ragioni che rendono necessaria la conferenza di servizi..."
          />
          <TextArea
            label="Amministrazioni coinvolte * (una per riga)"
            rows={4}
            value={conferenzaForm.amministrazioni_coinvolte}
            onChange={(e) => setConferenzaForm((f) => ({ ...f, amministrazioni_coinvolte: e.target.value }))}
            placeholder={"Comune di Roma\nProvincia di Roma\nSoprintendenza alle Belle Arti"}
          />
          <Select
            label="Termine per le determinazioni *"
            value={conferenzaForm.termine_determinazioni_giorni}
            onChange={(e) => setConferenzaForm((f) => ({ ...f, termine_determinazioni_giorni: e.target.value }))}
            options={[
              { value: "30", label: "30 giorni (ordinario)" },
              { value: "60", label: "60 giorni (tutele ambientali/paesaggistiche)" },
            ]}
          />
          {conferenzaErrors?.length > 0 && <NotificationsList errors={conferenzaErrors} />}
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <SimpleButton text="Annulla" customFunc={() => { setOpenConferenza(false); setConferenzaErrors(null); }} />
            <LoadingButton isLoading={saving} customFunc={handleAttivaConferenza} text="Attiva conferenza" />
          </div>
        </div>
      </PppDialog>
    </div>
  );
}
